/**
 * Servicio de gestión de tokens para el sistema de facturación electrónica
 */

class TokenStorage {
  constructor() {
    this.TOKEN_KEY = 'fedelza_auth_token';
    this.USER_KEY = 'fedelza_user_data';
    this.AUTH_KEY = 'auth'; 
  }

  // ============= GESTIÓN DE TOKENS =============

  /**
   * Obtiene el token de acceso del localStorage
   */
  getToken() {
    try {
      return localStorage.getItem(this.TOKEN_KEY);
    } catch (error) {
      return null;
    }
  }

  /**
   * Almacena el token de acceso en localStorage
   */
  setToken(token) {
    try {
      if (!token) {
        return false;
      }
      
      localStorage.setItem(this.TOKEN_KEY, token);
      localStorage.setItem(this.AUTH_KEY, 'true'); // Para compatibilidad
      console.log('✅ Token almacenado correctamente');
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Elimina el token de acceso del localStorage
   */
  clearToken() {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.AUTH_KEY);
      console.log(' Token eliminado');
    } catch (error) {
      
    }
  }

  // ============= GESTIÓN DE DATOS DE USUARIO =============

  /**
   * Obtiene los datos del usuario autenticado
   */
  getUserData() {
    try {
      const userData = localStorage.getItem(this.USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      
      return null;
    }
  }

  /**
   * Almacena los datos del usuario autenticado
   */
  setUserData(userData) {
    try {
      if (!userData) {
        
        return false;
      }
      
      localStorage.setItem(this.USER_KEY, JSON.stringify(userData));
      localStorage.setItem('userData', JSON.stringify(userData)); 
      
      return true;
    } catch (error) {
      
      return false;
    }
  }

  /**
   * Elimina los datos del usuario del localStorage
   */
  clearUserData() {
    try {
      localStorage.removeItem(this.USER_KEY);
      localStorage.removeItem('userData'); 
     
    } catch (error) {
      
    }
  }

  // ============= UTILIDADES Y VALIDACIONES =============

  /**
   * Verifica si existe un token válido
   */
  hasValidToken() {
    const token = this.getToken();
    return token && token.length > 0;
  }

  /**
   * Verifica si el token ha expirado (decodifica JWT)
   */
  isTokenExpired() {
    try {
      const token = this.getToken();
      if (!token) return true;

      // Decodificar el payload del JWT
      const base64Payload = token.split('.')[1];
      if (!base64Payload) return true;

      const payload = JSON.parse(atob(base64Payload));
      const currentTime = Date.now() / 1000;
      
      // Verificar si el token ha expirado
      return payload.exp && payload.exp < currentTime;
    } catch (error) {
     
      return true; // Asumir expirado si hay error
    }
  }

  /**
   * Obtiene la información del usuario desde el token JWT
   */
  getUserFromToken() {
    try {
      const token = this.getToken();
      if (!token) return null;

      const base64Payload = token.split('.')[1];
      if (!base64Payload) return null;

      return JSON.parse(atob(base64Payload));
    } catch (error) {
     
      return null;
    }
  }

  /**
   * Limpia toda la información de autenticación
   */
  clearAll() {
    this.clearToken();
    this.clearUserData();
    // También limpiar todo el localStorage para compatibilidad
    try {
      localStorage.clear();
      
    } catch (error) {
     
    }
  }

  /**
   * Almacena toda la información de autenticación después del login
   */
  setAuthData({ token, userData, user }) {
    try {
      let success = true;
      
      if (token) {
        success = success && this.setToken(token);
      }
      
      // Manejar tanto 'userData' como 'user' para flexibilidad
      const userInfo = userData || user;
      if (userInfo) {
        success = success && this.setUserData(userInfo);
      }
      
      if (success) {
       
      }
      
      return success;
    } catch (error) {
     
      return false;
    }
  }

  /**
   * Obtiene toda la información de autenticación
   */
  getAuthData() {
    return {
      token: this.getToken(),
      userData: this.getUserData(),
      isAuthenticated: this.hasValidToken() && !this.isTokenExpired(),
      isValid: this.hasValidToken() && !this.isTokenExpired()
    };
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isAuthenticated() {
    return this.hasValidToken() && !this.isTokenExpired();
  }

  /**
   * Obtiene el header Authorization para las peticiones API
   */
  getAuthHeader() {
    const token = this.getToken();
    return token ? `Bearer ${token}` : null;
  }
}

// Crear instancia singleton
const tokenStorage = new TokenStorage();

export default tokenStorage;

// También exportar métodos individuales para compatibilidad
export const {
  getToken,
  setToken,
  clearToken,
  getUserData,
  setUserData,
  clearUserData,
  hasValidToken,
  isTokenExpired,
  getUserFromToken,
  clearAll,
  setAuthData,
  getAuthData,
  isAuthenticated,
  getAuthHeader
} = tokenStorage;